import type { SkillDef } from "@/mocks/pipeline";

// disciplineOrder is the locked facet order for palette groups.
const disciplineOrder = [
  "Engineering", "SEO", "Strategy", "Content", "Paid Ads", "Analytics", "Social",
  "Design", "Productivity", "Security",
  "AI/ML", "API", "Research", "Pstack", "Media",
];

export interface PaletteGroup {
  discipline: string;
  skills: SkillDef[];
}

// paletteGroups buckets skills by discipline in the locked facet order.
export function paletteGroups(skills: SkillDef[]): PaletteGroup[] {
  const groups = new Map<string, SkillDef[]>();

  for (const s of skills) {
    const disc = s.discipline || "Uncategorized";
    if (!groups.has(disc)) {
      groups.set(disc, []);
    }
    groups.get(disc)!.push(s);
  }

  // Sort by discipline order, Uncategorized last.
  const sorted = Array.from(groups.entries()).sort((a, b) => {
    const aIdx = disciplineOrder.indexOf(a[0]);
    const bIdx = disciplineOrder.indexOf(b[0]);
    const aUncat = a[0] === "Uncategorized" ? 999 : aIdx;
    const bUncat = b[0] === "Uncategorized" ? 999 : bIdx;
    if (aUncat !== bUncat) return aUncat - bUncat;
    if (aIdx !== bIdx) return aIdx - bIdx;
    return a[0].localeCompare(b[0]);
  });

  return sorted.map(([discipline, skills]) => ({ discipline, skills }));
}

// groupStartsOpen returns whether a discipline group starts expanded.
export function groupStartsOpen(_discipline: string): boolean {
  return false;
}

// matchesPaletteQuery checks if a skill matches the search query.
export function matchesPaletteQuery(skill: SkillDef, query: string): boolean {
  if (!query.trim()) return true;
  const q = query.toLowerCase();
  if (skill.name.toLowerCase().includes(q)) return true;
  if ((skill.category || "").toLowerCase().includes(q)) return true;
  if ((skill.discipline || "").toLowerCase().includes(q)) return true;
  if ((skill.description || "").toLowerCase().includes(q)) return true;
  for (const inp of skill.inputs || []) {
    if (inp.type.toLowerCase().includes(q)) return true;
  }
  for (const out of skill.outputs || []) {
    if (out.type.toLowerCase().includes(q)) return true;
  }
  return false;
}

// ioCardLabel returns the I/O summary for a skill card.
export function ioCardLabel(skill: SkillDef): string {
  const inCount = skill.inputs?.length ?? 0;
  const outCount = skill.outputs?.length ?? 0;
  if (inCount === 0 && outCount === 0) return "no I/O";
  return `in ${inCount} / out ${outCount}`;
}
